import { Box } from '@mui/material';
import React from 'react';
import { CreateInvestmentEntityRequest, EntityType } from '../../models/InvestmentEntity';
import CompactCardSelector from '../common/CompactCardSelector';
import { StyledButton } from '../styles/StyledButtons';
import { StyledInput } from '../styles/StyledInputs';
import { StyledCard, StyledCardContent, StyledHeading, StyledTypography } from '../styles/styles';
import { ENTITY_TYPE_OPTIONS } from './createEntityStepConstants';
import { EntitySpecificFieldInput } from './EntitySpecificFieldInput';
import { TrusteeCorporateSection } from './TrusteeCorporateSection';
import { TrusteeIndividualsSection } from './TrusteeIndividualsSection';
import { useCreateEntityForm } from './useCreateEntityForm';

interface CreateEntityStepProps {
  onEntityCreated: (entity: CreateInvestmentEntityRequest) => Promise<void>;
  onCancel: () => void;
  initialData?: CreateInvestmentEntityRequest;
  validationErrors?: { [key: string]: string };
}

export const CreateEntityStep: React.FC<CreateEntityStepProps> = ({
  onEntityCreated,
  onCancel,
  initialData,
  validationErrors,
}) => {
  const {
    formData,
    errors,
    allErrors,
    entitySpecificFields,
    trusteeIndividuals,
    trusteeCorporate,
    focusRefs,
    trusteeFirstNameRef,
    handleInputChange,
    handleSpecificFieldChange,
    addTrusteeIndividual,
    removeTrusteeIndividual,
    updateTrusteeIndividual,
    updateTrusteeCorporate,
    handleSubmit,
    showTrusteeIndividuals,
    showTrusteeCorporate,
    isFormValid,
  } = useCreateEntityForm({ initialData, validationErrors, onEntityCreated });

  const entityTypeLabel = ENTITY_TYPE_OPTIONS.find((t) => t.value === formData.type)?.label;

  return (
    <StyledCard tone="tone1">
      <StyledCardContent sx={{ py: 3 }}>
        <StyledHeading headerVariant="heading2" sx={{ mb: 3 }}>
          Create New Investment Entity
        </StyledHeading>

        <StyledTypography sx={{ mb: 4 }}>
          Select the entity type and provide the required details. The entity name will be
          automatically generated based on the information you provide.
        </StyledTypography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <CompactCardSelector
            cardSize="small"
            label="Entity Type"
            required
            options={ENTITY_TYPE_OPTIONS.map((type) => ({
              value: type.value,
              label: type.label,
            }))}
            value={formData.type ?? ''}
            onChange={(value) => handleInputChange('type', value as EntityType)}
            error={errors.type}
          />

          <StyledInput
            label="Description (Optional)"
            value={formData.description || ''}
            onChange={(e) => handleInputChange('description', e.target.value)}
            multiline
            rows={3}
            fullWidth
            helperText="Provide any additional details about this entity"
          />

          {entitySpecificFields.length > 0 && (
            <>
              <StyledTypography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                {entityTypeLabel} Specific Information
              </StyledTypography>
              {entitySpecificFields.map((field) => (
                <EntitySpecificFieldInput
                  key={field.key}
                  field={field}
                  value={(formData as Record<string, string | number | undefined>)[field.key] || ''}
                  error={allErrors[field.key]}
                  onChange={handleSpecificFieldChange}
                  focusRefs={focusRefs}
                />
              ))}
            </>
          )}

          {showTrusteeIndividuals && (
            <TrusteeIndividualsSection
              trusteeIndividuals={trusteeIndividuals}
              trusteeIndividualsError={errors.trusteeIndividuals}
              allErrors={allErrors}
              trusteeFirstNameRef={trusteeFirstNameRef}
              onAdd={addTrusteeIndividual}
              onRemove={removeTrusteeIndividual}
              onUpdate={updateTrusteeIndividual}
            />
          )}

          {showTrusteeCorporate && (
            <TrusteeCorporateSection
              trusteeCorporate={trusteeCorporate}
              trusteeCorporateError={errors.trusteeCorporate}
              allErrors={allErrors}
              onUpdate={updateTrusteeCorporate}
            />
          )}

          <Box sx={{ display: 'flex', gap: 2, pt: 2 }}>
            <StyledButton variant="contained" onClick={handleSubmit} disabled={!isFormValid}>
              Create Entity
            </StyledButton>
            <StyledButton variant="outlined" onClick={onCancel}>
              Cancel
            </StyledButton>
          </Box>
        </Box>
      </StyledCardContent>
    </StyledCard>
  );
};
